import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../api/apiService';
import type { User } from '../../types';
import { Table } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { useToast } from '../../contexts/ToastContext';
import './AdminUsers.css';

export const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [createForm, setCreateForm] = useState({ name: '', email: '', username: '', password: '', role: 'user' as 'user' | 'coach' | 'admin' });
  const [editForm, setEditForm] = useState({ name: '', email: '', role: 'user' as 'user' | 'coach' | 'admin' });
  const [newPassword, setNewPassword] = useState('');
  const { t } = useTranslation();
  const toast = useToast();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await apiService.getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error(t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setEditForm({ name: user.name, email: user.email, role: user.role });
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handlePasswordClick = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setIsPasswordModalOpen(true);
  };

  const handleCreateSubmit = async () => {
    try {
      await apiService.createUser(createForm);
      toast.success(t('userCreated'));
      setIsCreateModalOpen(false);
      setCreateForm({ name: '', email: '', username: '', password: '', role: 'user' });
      loadUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(t('error'));
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedUser) return;

    try {
      await apiService.updateUser(selectedUser.id, editForm);
      toast.success(t('userUpdated'));
      setIsEditModalOpen(false);
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(t('error'));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    try {
      await apiService.deleteUser(selectedUser.id);
      toast.success(t('userDeleted'));
      setIsDeleteModalOpen(false);
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(t('error'));
    }
  };

  const handlePasswordSubmit = async () => {
    if (!selectedUser || !newPassword) return;

    try {
      await apiService.changeUserPassword(selectedUser.id, newPassword);
      toast.success(t('passwordUpdated'));
      setIsPasswordModalOpen(false);
      setNewPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error(t('error'));
    }
  };

  const columns = [
    {
      header: t('name'),
      accessor: 'name' as keyof User,
    },
    {
      header: t('email'),
      accessor: 'email' as keyof User,
    },
    {
      header: t('role'),
      accessor: ((user: User) => (
        <span className={`user-role role-${user.role}`}>
          {t(user.role)}
        </span>
      )) as any,
    },
    {
      header: t('actions'),
      accessor: ((user: User) => (
        <div className="table-actions">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleEditClick(user)}
          >
            {t('edit')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => handlePasswordClick(user)}
          >
            {t('changeUserPassword')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDeleteClick(user)}
          >
            {t('delete')}
          </Button>
        </div>
      )) as any,
      className: 'table-actions-column',
    },
  ];

  if (isLoading) {
    return <div className="text-center p-4">{t('loading')}</div>;
  }

  return (
    <div className="admin-users-page">
      <div className="page-header">
        <h1 className="page-title">{t('users')}</h1>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          {t('createUser')}
        </Button>
      </div>

      <Table
        columns={columns}
        data={users}
        keyExtractor={(user) => user.id.toString()}
      />

      {/* Create User Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title={t('createUser')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleCreateSubmit}>{t('create')}</Button>
          </>
        }
      >
        <div className="create-user-form">
          <Input
            label={t('name')}
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            required
          />
          <Input
            label={t('email')}
            type="email"
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            required
          />
          <Input
            label={t('username')}
            value={createForm.username}
            onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
            required
          />
          <Input
            label={t('password')}
            type="password"
            value={createForm.password}
            onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            required
          />
          <Select
            label={t('role')}
            value={createForm.role}
            onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as 'user' | 'coach' | 'admin' })}
            options={[
              { value: 'user', label: t('user') },
              { value: 'coach', label: t('coach') },
              { value: 'admin', label: t('admin') },
            ]}
          />
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title={t('editUser')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleEditSubmit}>{t('save')}</Button>
          </>
        }
      >
        <div className="edit-user-form">
          <Input
            label={t('name')}
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          />
          <Input
            label={t('email')}
            type="email"
            value={editForm.email}
            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
          />
          <Select
            label={t('role')}
            value={editForm.role}
            onChange={(e) => setEditForm({ ...editForm, role: e.target.value as 'user' | 'coach' | 'admin' })}
            options={[
              { value: 'user', label: t('user') },
              { value: 'coach', label: t('coach') },
              { value: 'admin', label: t('admin') },
            ]}
          />
        </div>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        title={t('changeUserPassword')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsPasswordModalOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handlePasswordSubmit} disabled={!newPassword}>
              {t('save')}
            </Button>
          </>
        }
      >
        <div className="password-form">
          {selectedUser && (
            <div className="user-info">
              <p><strong>{t('name')}:</strong> {selectedUser.name}</p>
              <p><strong>{t('email')}:</strong> {selectedUser.email}</p>
            </div>
          )}
          <Input
            label={t('newPassword')}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Ingrese la nueva contraseña"
          />
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t('deleteUser')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              {t('cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm}>
              {t('delete')}
            </Button>
          </>
        }
      >
        <p>{t('confirmDeleteUser')}</p>
        {selectedUser && (
          <div className="user-info">
            <p><strong>{t('name')}:</strong> {selectedUser.name}</p>
            <p><strong>{t('email')}:</strong> {selectedUser.email}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};
